import React from 'react';
import ModernRecipeCard from '@/components/ModernRecipeCard';
import RecipeFilterToolbar from '@/components/RecipeFilterToolbar';
import { useRecipeStore } from '@/stores/recipeStore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Search, Star, ChefHat, Sparkles, ArrowRight } from 'lucide-react';

const EmptyState: React.FC<{ 
  type: 'no-results' | 'no-favorites' | 'no-recipes';
  searchQuery?: string;
  onClearFilters?: () => void;
}> = ({ type, searchQuery, onClearFilters }) => {
  const configs = {
    'no-results': {
      icon: Search,
      title: 'No recipes found',
      description: searchQuery 
        ? `No recipes match "${searchQuery}". Try a different search term.`
        : 'No recipes match your current filters.',
      action: onClearFilters ? { label: 'Clear filters', onClick: onClearFilters } : null
    },
    'no-favorites': {
      icon: Star,
      title: 'No favorites yet',
      description: 'Star recipes you love to find them quickly here.',
      action: null
    },
    'no-recipes': {
      icon: ChefHat,
      title: 'Your cookbook awaits',
      description: 'Create your first recipe to get started.',
      action: null
    }
  };

  const config = configs[type];
  const Icon = config.icon;

  return (
    <div className="flex flex-col items-center justify-center py-16 sm:py-24 px-4">
      <div className="w-16 h-16 rounded-full bg-stone-100 dark:bg-stone-800 flex items-center justify-center mb-6">
        <Icon className="w-8 h-8 text-stone-400 dark:text-stone-500" />
      </div>
      <h3 className="text-lg font-semibold text-stone-900 dark:text-stone-100 mb-2">
        {config.title}
      </h3>
      <p className="text-sm text-stone-500 dark:text-stone-400 text-center max-w-sm mb-6">
        {config.description}
      </p>
      {config.action && (
        <button
          onClick={config.action.onClick}
          className="text-sm font-medium text-terracotta-600 dark:text-terracotta-400 hover:text-terracotta-700 dark:hover:text-terracotta-300 transition-colors"
        >
          {config.action.label}
        </button>
      )}
    </div>
  );
};

const GridSkeleton: React.FC = () => (
  <div className="grid gap-5 sm:gap-6 lg:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="animate-pulse">
        <div className="aspect-[4/3] rounded-xl bg-stone-200 dark:bg-stone-800" />
        <div className="mt-3 h-4 bg-stone-200 dark:bg-stone-800 rounded w-3/4" />
        <div className="mt-2 h-3 bg-stone-200 dark:bg-stone-800 rounded w-1/2" />
      </div>
    ))}
  </div>
);

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { role } = useAuth();
  const {
    studioRecipes,
    loading,
    getFilteredRecipes,
    toggleFavorite,
    toggleShare,
    deleteRecipe,
    filters,
    pagination,
    setSearchQuery,
    setCategory,
    setShowFavoritesOnly,
    setShowOwnRecipesOnly,
    setLimit,
    nextPage,
    previousPage,
  } = useRecipeStore();

  const filteredRecipes = getFilteredRecipes();

  const handleRecipeClick = (recipe: any) => {
    navigate(`/recipe/${recipe.id}`);
  };

  const handleFavoriteToggle = async (recipe: any) => {
    try {
      await toggleFavorite(recipe.id);
    } catch {
    }
  };

  const handleShareToggle = async (recipe: any) => {
    try {
      await toggleShare(recipe.id, recipe.share);
    } catch {
    }
  };

  const handleDeleteRecipe = async (recipeId: string) => {
    try {
      if (window.confirm('Are you sure you want to delete this recipe? This action cannot be undone.')) {
        await deleteRecipe(recipeId);
      }
    } catch {
    }
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setCategory('all');
    setShowFavoritesOnly(false);
    setShowOwnRecipesOnly(false);
  };

  const getEmptyStateType = (): 'no-results' | 'no-favorites' | 'no-recipes' | null => {
    if (filteredRecipes.length > 0) return null;
    if (filters.showFavoritesOnly) return 'no-favorites';
    if (filters.searchQuery || filters.category !== 'all' || filters.showOwnRecipesOnly) return 'no-results';
    return 'no-recipes';
  };

  const emptyStateType = getEmptyStateType();

  return (
    <div
      className="fixed top-[70px] left-0 right-0 bottom-0 bg-[#FAF7F2] dark:bg-[#1A1714]"
      style={{
        overflowY: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      <div className="min-h-full">
        {/* Studio Section */}
        {studioRecipes.length > 0 && (
          <section className="px-4 sm:px-6 lg:px-8 pt-6 pb-8">
            <div className="max-w-7xl mx-auto rounded-2xl border border-terracotta-200/60 dark:border-terracotta-900/40 bg-gradient-to-br from-terracotta-50/70 via-amber-50/40 to-stone-50 dark:from-terracotta-950/30 dark:via-stone-900/40 dark:to-stone-900 px-4 sm:px-5 lg:px-6 py-5 sm:py-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                  <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-terracotta-500 text-white shadow-sm">
                    <Sparkles className="h-4 w-4" />
                  </span>
                  <div>
                    <h2 className="text-base sm:text-lg font-semibold text-stone-900 dark:text-stone-100 leading-tight">
                      Fresh from the Studio
                    </h2>
                    <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5 hidden sm:block">
                      AI-generated recipes from our chef personas
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/studio')}
                  className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-sm font-medium text-terracotta-700 dark:text-terracotta-300 bg-white/70 dark:bg-stone-800/60 hover:bg-white dark:hover:bg-stone-800 border border-terracotta-200/60 dark:border-terracotta-900/40 hover:shadow-sm transition-all"
                >
                  Try the Studio
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="grid gap-5 sm:gap-6 lg:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {studioRecipes.slice(0, 4).map((recipe: any) => (
                  <ModernRecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    onClick={() => handleRecipeClick(recipe)}
                    onFavoriteToggle={() => handleFavoriteToggle(recipe)}
                    onShareToggle={() => handleShareToggle(recipe)}
                    onDelete={() => handleDeleteRecipe(recipe.id)}
                    isAdmin={role === 'admin'}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Recipes Section */}
        <div className="px-4 sm:px-6 lg:px-8 pb-8">
          <div className="max-w-7xl mx-auto">
            {/* Toolbar */}
            <div className="mb-6">
              <RecipeFilterToolbar
                filters={filters}
                pagination={pagination}
                loading={loading}
                onSearchChange={setSearchQuery}
                onCategoryChange={setCategory}
                onFavoritesToggle={setShowFavoritesOnly}
                onShowOwnRecipesOnlyToggle={setShowOwnRecipesOnly}
                onLimitChange={setLimit}
                onNextPage={nextPage}
                onPreviousPage={previousPage}
                showActions={false}
                variant="home"
              />
            </div>
            
            {/* Grid */}
            {loading ? (
              <GridSkeleton />
            ) : emptyStateType ? (
              <EmptyState 
                type={emptyStateType} 
                searchQuery={filters.searchQuery}
                onClearFilters={clearAllFilters}
              />
            ) : (
              <div className="grid gap-5 sm:gap-6 lg:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredRecipes.map((recipe: any) => (
                  <ModernRecipeCard
                    key={recipe.id}
                    recipe={recipe}
                    onClick={() => handleRecipeClick(recipe)}
                    onFavoriteToggle={() => handleFavoriteToggle(recipe)}
                    onShareToggle={() => handleShareToggle(recipe)}
                    onDelete={() => handleDeleteRecipe(recipe.id)}
                    isAdmin={role === 'admin'}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
